import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ReactNode } from "react";

interface TanStackQueryProviderProps {
  children: ReactNode;
}

// Create QueryClient outside component to avoid recreation on every render
// This is the current recommended approach for client-side only React apps
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Similar to SWR's default revalidation behavior
      staleTime: 0, // Data is immediately considered stale (like SWR)
      gcTime: 5 * 60 * 1000, // 5 minutes (SWR default cache time)
      refetchOnWindowFocus: true, // Similar to SWR's revalidateOnFocus
      refetchOnReconnect: true, // Similar to SWR's revalidateOnReconnect
      retry: (failureCount) => failureCount < 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000) // Exponential backoff
    },
    mutations: {
      retry: false // Don't retry mutations by default
    }
  }
});

export const TanStackQueryProvider = ({ children }: TanStackQueryProviderProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Only show devtools in development */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};
